import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  CheckCircle, 
  XCircle, 
  Eye, 
  Clock,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { Item, ItemContent, ItemDescription } from '@/components/ui/item';
import {
  Document,
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_SUBTYPE_LABELS,
  type DocumentSubType,
} from '@/types/document';
import { DocumentStatusBadge } from './DocumentStatusBadge';
import { formatAdminBusinessEt } from '@/lib/admin-datetime';

interface DocumentItemProps {
  document: Document;
  onApprove: (
    documentId: string,
    notes?: string,
    target?: 'document' | 'pending_update',
  ) => void;
  onReject: (
    documentId: string,
    reason: string,
    notes?: string,
    target?: 'document' | 'pending_update',
  ) => void;
  onView: (document: Document, opts?: { pending?: boolean }) => void;
  isUpdating: boolean;
  isViewing?: boolean;
}

export const DocumentItem: React.FC<DocumentItemProps> = ({
  document,
  onApprove,
  onReject,
  onView,
  isUpdating,
  isViewing = false
}) => {
  const [showNotes, setShowNotes] = React.useState(false);
  const [notes, setNotes] = React.useState('');
  const [rejectionReason, setRejectionReason] = React.useState('');
  const [actionType, setActionType] = React.useState<
    'approve' | 'reject' | 'approve_update' | 'reject_update' | null
  >(null);

  const hasPendingUpdate = Boolean(document.hasPendingUpdate);

  const handleConfirm = () => {
    if (actionType === 'approve') {
      onApprove(document._id, notes.trim() || undefined, 'document');
    } else if (actionType === 'approve_update') {
      onApprove(document._id, notes.trim() || undefined, 'pending_update');
    } else if (actionType === 'reject') {
      if (!rejectionReason.trim()) return;
      onReject(document._id, rejectionReason.trim(), notes.trim() || undefined, 'document');
    } else if (actionType === 'reject_update') {
      if (!rejectionReason.trim()) return;
      onReject(
        document._id,
        rejectionReason.trim(),
        notes.trim() || undefined,
        'pending_update',
      );
    }
    setNotes('');
    setRejectionReason('');
    setShowNotes(false);
    setActionType(null);
  };

  const handleActionClick = (
    type: 'approve' | 'reject' | 'approve_update' | 'reject_update',
  ) => {
    setActionType(type);
    setShowNotes(true);
  };

  const canApprove = document.status === 'UPLOADED' && !hasPendingUpdate;
  const canRejectDocument =
    (document.status === 'UPLOADED' || document.status === 'APPROVED') &&
    !hasPendingUpdate;
  const canView =
    document.status === 'UPLOADED' ||
    document.status === 'APPROVED' ||
    document.status === 'REJECTED';

  const getStatusIcon = () => {
    if (hasPendingUpdate) {
      return <RefreshCw className="h-4 w-4 text-amber-600" />;
    }
    switch (document.status) {
      case 'APPROVED':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'REJECTED':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'UPLOADED':
        return <Clock className="h-4 w-4 text-blue-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const subTypeLabel =
    document.subTypeLabel ??
    (document.subType
      ? DOCUMENT_SUBTYPE_LABELS[document.subType as DocumentSubType] ?? null
      : null);

  const needsRejectReason =
    actionType === 'reject' || actionType === 'reject_update';

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            {getStatusIcon()}
            <span>
              {document.documentType === 'OTHER' && document.description?.trim()
                ? document.description.trim()
                : DOCUMENT_TYPE_LABELS[document.documentType]}
            </span>
            {document.documentType === 'OTHER' && (
              <Badge
                variant="secondary"
                className="text-[10px] font-normal uppercase tracking-wide"
              >
                Other
              </Badge>
            )}
            {subTypeLabel && (
              <Badge
                variant="secondary"
                className="text-[10px] font-normal uppercase tracking-wide"
              >
                {subTypeLabel}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <DocumentStatusBadge status={document.status} />
            {hasPendingUpdate && (
              <Badge variant="outline" className="text-xs border-amber-300 text-amber-800">
                Update pending
              </Badge>
            )}
            {document.hasBeenUpdated && !hasPendingUpdate && (
              <Badge variant="outline" className="text-xs border-emerald-300 text-emerald-800">
                Updated
              </Badge>
            )}
            {document.isRequired && (
              <Badge variant="outline" className="text-xs">
                Required
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
            <div>
              <span className="font-medium">Uploaded:</span>{' '}
              {formatAdminBusinessEt(document.uploadedAt)}
            </div>
            {document.approvedAt && (
              <div>
                <span className="font-medium">Approved:</span>{' '}
                {formatAdminBusinessEt(document.approvedAt)}
              </div>
            )}
            {hasPendingUpdate && document.pendingUploadedAt && (
              <div className="col-span-2">
                <span className="font-medium">Replacement submitted:</span>{' '}
                {formatAdminBusinessEt(document.pendingUploadedAt)}
              </div>
            )}
          </div>

          {hasPendingUpdate && (
            <Item variant="outline" className="border-amber-200 bg-amber-50/40">
              <ItemContent>
                <ItemDescription className="text-amber-900">
                  <span className="font-medium">Replacement awaiting review.</span>{' '}
                  The currently approved file stays active until you approve the
                  update. You can view both files below.
                </ItemDescription>
              </ItemContent>
            </Item>
          )}

          {document.status === 'APPROVED' && !hasPendingUpdate && (
            <Item variant="outline" className="border-green-200 bg-green-50/30">
              <ItemContent>
                <ItemDescription className="text-green-800">
                  <span className="font-medium">✓ Approved</span> — locked for the
                  student. Reject only if they must upload a different document type.
                </ItemDescription>
              </ItemContent>
            </Item>
          )}

          {document.status === 'REJECTED' && document.rejectionReason && (
            <Item variant="outline" className="border-red-200 bg-red-50/30">
              <ItemContent>
                <ItemDescription className="text-red-800">
                  <span className="font-medium">Rejection Reason:</span>{' '}
                  {document.rejectionReason}
                </ItemDescription>
              </ItemContent>
            </Item>
          )}

          {!hasPendingUpdate &&
            document.status === 'APPROVED' &&
            document.rejectionReason?.startsWith('Update rejected:') && (
              <Item variant="outline" className="border-amber-200 bg-amber-50/30">
                <ItemContent>
                  <ItemDescription className="text-amber-900">
                    <span className="font-medium">Last update note:</span>{' '}
                    {document.rejectionReason}
                  </ItemDescription>
                </ItemContent>
              </Item>
            )}

          {document.notes && (
            <Item variant="outline" className="border-blue-200 bg-blue-50/30">
              <ItemContent>
                <ItemDescription className="text-blue-800">
                  <span className="font-medium">Notes:</span> {document.notes}
                </ItemDescription>
              </ItemContent>
            </Item>
          )}

          <div className="flex flex-wrap items-center gap-2 pt-2">
            {canView && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onView(document)}
                disabled={isViewing}
                className="flex items-center gap-2"
              >
                <Eye className="h-4 w-4" />
                {isViewing ? 'Loading...' : hasPendingUpdate ? 'View current' : 'View'}
              </Button>
            )}

            {hasPendingUpdate && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onView(document, { pending: true })}
                disabled={isViewing}
                className="flex items-center gap-2"
              >
                <Eye className="h-4 w-4" />
                View replacement
              </Button>
            )}

            {canApprove && (
              <Button
                variant="default"
                size="sm"
                onClick={() => handleActionClick('approve')}
                disabled={isUpdating}
                className="flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                Approve
              </Button>
            )}

            {hasPendingUpdate && (
              <>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => handleActionClick('approve_update')}
                  disabled={isUpdating}
                  className="flex items-center gap-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  Approve update
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleActionClick('reject_update')}
                  disabled={isUpdating}
                  className="flex items-center gap-2"
                >
                  <XCircle className="h-4 w-4" />
                  Reject update
                </Button>
              </>
            )}

            {canRejectDocument && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleActionClick('reject')}
                disabled={isUpdating}
                className="flex items-center gap-2"
              >
                <XCircle className="h-4 w-4" />
                Reject
              </Button>
            )}
          </div>

          {showNotes && (
            <div className="space-y-3 p-4 border rounded-lg bg-muted/50">
              <div>
                <label className="text-sm font-medium">Notes (Optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes about this document..."
                  className="w-full mt-1 p-2 text-sm border rounded-md resize-none"
                  rows={2}
                />
              </div>

              {needsRejectReason && (
                <div>
                  <label className="text-sm font-medium text-red-600">
                    {actionType === 'reject_update'
                      ? 'Why is this replacement rejected? *'
                      : 'Rejection Reason *'}
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder={
                      actionType === 'reject_update'
                        ? 'Explain why the replacement cannot be used…'
                        : 'Explain why this document is being rejected...'
                    }
                    className="w-full mt-1 p-2 text-sm border border-red-200 rounded-md resize-none"
                    rows={2}
                    required
                  />
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={handleConfirm}
                  disabled={
                    isUpdating ||
                    (needsRejectReason && !rejectionReason.trim())
                  }
                  variant={needsRejectReason ? 'destructive' : 'default'}
                >
                  {actionType === 'approve'
                    ? 'Approve'
                    : actionType === 'approve_update'
                      ? 'Approve update'
                      : actionType === 'reject_update'
                        ? 'Reject update'
                        : 'Reject'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowNotes(false);
                    setNotes('');
                    setRejectionReason('');
                    setActionType(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
